import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Redo,
  RemoveFormatting,
  Underline,
  Undo,
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { safeUrl, sanitizeHtml } from "@/lib/sanitize";
import { cn } from "@/lib/utils";

export interface RichTextEditorHandle {
  /** Insert plain text (e.g. a `{merge_token}`) at the current cursor position. */
  insertText: (text: string) => void;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Forwarded to the contenteditable so a <Label htmlFor> can target it. */
  id?: string;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  onFocus?: () => void;
  /** Min height of the editing area in px (default 220). */
  minHeight?: number;
  /** Read-only mode (a disabled <fieldset> does NOT reach contenteditable). */
  disabled?: boolean;
}

function promptForLink(editor: Editor) {
  const existing = (editor.getAttributes("link").href as string | undefined) ?? "";
  // window.prompt keeps the toolbar dependency-free; the URL is scheme-guarded.
  const input = window.prompt("Link URL", existing);
  if (input === null) return; // cancelled
  const href = safeUrl(input);
  if (!input.trim() || href === "#") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
}

/**
 * Shared TipTap WYSIWYG for rich "body" fields (What's New, FAQ answers, letter/
 * email templates, e-sign invitation, signature, practitioner about). Every value
 * handed to the parent is DOMPurify-sanitized first, so the persisted/rendered
 * HTML is always clean regardless of pasted content. An empty document is emitted
 * as `""` (not `<p></p>`) so required-field validation keeps working.
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    { value, onChange, id, ariaLabel, ariaInvalid, onFocus, minHeight = 220, disabled },
    ref,
  ) {
    const editor = useEditor({
      editable: !disabled,
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          link: { openOnClick: false, defaultProtocol: "https" },
        }),
      ],
      content: value,
      onUpdate: ({ editor }) =>
        onChange(editor.isEmpty ? "" : sanitizeHtml(editor.getHTML())),
      editorProps: {
        attributes: {
          ...(id ? { id } : {}),
          ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
          class: "prose prose-sm dark:prose-invert max-w-none p-3 focus:outline-none",
          style: `min-height: ${minHeight}px`,
        },
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        insertText: (text: string) => {
          editor?.chain().focus().insertContent(text).run();
        },
      }),
      [editor],
    );

    useEffect(() => {
      // emitUpdate=false: editability changes (and this effect's mount run)
      // must not fire onUpdate — a mount-time onChange would spuriously flip
      // Formik dirty/touched on forms whose Save is gated on !dirty.
      editor?.setEditable(!disabled, false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    // Keep the editor in sync when the value is reset externally (e.g. Clear).
    // Skipped while focused: sanitize can legitimately differ from getHTML()
    // (link rel, etc.) and a mid-typing setContent would jump the caret.
    useEffect(() => {
      // Guard isDestroyed: under React StrictMode the first editor instance is
      // torn down and this effect can re-run against it; calling getHTML() on a
      // destroyed editor reads a null schema and throws ("...reading 'cached'").
      if (editor && !editor.isDestroyed && !editor.isFocused && value !== editor.getHTML()) {
        editor.commands.setContent(value || "");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    if (!editor) return null;

    const btn = (active: boolean) => cn("size-8", active && "bg-accent text-primary");
    const headingButtons = [
      { level: 1 as const, icon: Heading1, label: "Heading 1" },
      { level: 2 as const, icon: Heading2, label: "Heading 2" },
      { level: 3 as const, icon: Heading3, label: "Heading 3" },
    ];

    return (
      <div
        className={cn(
          "border-input overflow-hidden rounded-md border",
          ariaInvalid && "border-destructive ring-destructive/20 ring-[3px]",
        )}
        onFocus={onFocus}
      >
        <div className="bg-muted/40 flex flex-wrap items-center gap-1 border-b p-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={btn(editor.isActive("bold"))}
            onClick={() => editor.chain().focus().toggleBold().run()}
            aria-label="Bold"
          >
            <Bold className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={btn(editor.isActive("italic"))}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            aria-label="Italic"
          >
            <Italic className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={btn(editor.isActive("underline"))}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            aria-label="Underline"
          >
            <Underline className="size-4" />
          </Button>
          <div className="bg-border mx-1 h-5 w-px" />
          {headingButtons.map(({ level, icon: Icon, label }) => (
            <Button
              key={level}
              type="button"
              variant="ghost"
              size="icon"
              className={btn(editor.isActive("heading", { level }))}
              onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
              aria-label={label}
            >
              <Icon className="size-4" />
            </Button>
          ))}
          <div className="bg-border mx-1 h-5 w-px" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={btn(editor.isActive("bulletList"))}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            aria-label="Bullet list"
          >
            <List className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={btn(editor.isActive("orderedList"))}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            aria-label="Numbered list"
          >
            <ListOrdered className="size-4" />
          </Button>
          <div className="bg-border mx-1 h-5 w-px" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={btn(editor.isActive("link"))}
            onClick={() => promptForLink(editor)}
            aria-label="Link"
          >
            <LinkIcon className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            aria-label="Clear formatting"
          >
            <RemoveFormatting className="size-4" />
          </Button>
          <div className="bg-border mx-1 h-5 w-px" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => editor.chain().focus().undo().run()}
            aria-label="Undo"
          >
            <Undo className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => editor.chain().focus().redo().run()}
            aria-label="Redo"
          >
            <Redo className="size-4" />
          </Button>
        </div>
        <EditorContent editor={editor} />
      </div>
    );
  },
);
